import axios from "axios";
import { apiBaseUrl } from "./backend";
import { logError } from "../utils/errors/logger";
import {
  aggregation,
  getProductListFragment,
  engineOilProductFragment,
} from "./fragments";

export const excludedQueryParams = [
  "page",
  "make",
  "model",
  "srsltid",
  "brightowl_code",
  "msclkid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "ref",
  "referrer",
  "src",
  "cmp",
  "campaignid",
  "adgroupid",
  "mc_cid",
  "mc_eid",
  "yclid",
  "_gl",
  "gclid",
  "gbraid",
  "gad_source",
  "gad_campaignid",
];

const getVariables = ({
  skuList,
  vFilter,
  catIdArr,
  sortOrder,
  filtersObj,
  currentPage,
  productPageSize,
}) => {
  let variables = {
    currentPage,
    productPageSize,
    productfilters: { price: { from: 0.01 }, category_id: { in: catIdArr } },
    productSort: {
      [sortOrder.field]: sortOrder.direction.toUpperCase(),
    },
  };

  if (skuList) {
    variables["productfilters"]["sku"] = { in: skuList };
  }

  if (vFilter?.session_id && vFilter?.component_id) {
    variables["productfilters"]["vrmFilter"] = vFilter;
  }

  for (const key in filtersObj) {
    if (!excludedQueryParams.includes(key)) {
      variables["productfilters"][key] = { in: filtersObj[key] };
    }
  }

  const hasMultipleCats = !vFilter && catIdArr?.length > 1;
  if (!hasMultipleCats) {
    variables["categoryfilters"] = { ids: { in: catIdArr } };
  }
  return { variables, hasMultipleCats };
};

export const getProductListing = async ({
  page,
  catIdArr,
  pageSize,
  sortOrder,
  specialCoupon,
  skuList = null,
  filtersObj = {},
  vFilter = null,
  isBrand = false,
}) => {
  try {
    const itemSchema = getProductListFragment();
    const { variables, hasMultipleCats } = getVariables({
      skuList,
      vFilter,
      catIdArr,
      sortOrder,
      filtersObj,
      currentPage: page,
      productPageSize: pageSize,
    });

    const query = `query ProductFilter (
      $currentPage: Int,
      $productPageSize: Int,
      $productSort: ProductAttributeSortInput,
      $productfilters: ProductAttributeFilterInput,
      $aggregationsFilter: AggregationsFilterInput
      ${hasMultipleCats ? "" : ", $categoryfilters: CategoryFilterInput"}
    ) {
      ${
        hasMultipleCats
          ? ""
          : `categoryList(filters: $categoryfilters) { id name ${
              isBrand ? "description" : ""
            }}`
      }
      products (
        sort: $productSort,
        filter: $productfilters,
        currentPage: $currentPage,
        pageSize: $productPageSize,
        specialCoupon: "${specialCoupon || ""}"
      ) {
        total_count
        items { ...Products }
        aggregations (filter: $aggregationsFilter) { ...AggregationFragment }
      }
    } ${itemSchema} ${aggregation}`;

    const axiosRes = await axios.get(apiBaseUrl(), {
      paramsSerializer: { indexes: null },
      params: {
        query,
        operationName: "categoryList",
        variables: JSON.stringify(variables),
      },
    });
    return axiosRes.data;
  } catch (error) {
    logError({
      error,
      message: "getProductListing",
      info: {
        page,
        isBrand,
        skuList,
        vFilter,
        catIdArr,
        pageSize,
        sortOrder,
        filtersObj,
        specialCoupon,
      },
    });
    return { data: [], errors: error };
  }
};

export const getEngineOilProducts = async ({
  catIdArr,
  sortOrder,
  currentPage,
  productPageSize,
  skuList = null,
  vFilter = null,
  queryParams = {},
  specialCoupon = "",
}) => {
  const { variables, hasMultipleCats } = getVariables({
    skuList,
    vFilter,
    catIdArr,
    sortOrder,
    currentPage,
    productPageSize,
    filtersObj: queryParams,
  });

  try {
    let query = `query ProductFilterEngineOil ($productPageSize: Int $currentPage: Int $productSort: ProductAttributeSortInput $productfilters: ProductAttributeFilterInput $aggregationsFilter: AggregationsFilterInput`;
    if (!hasMultipleCats) {
      query += ` $categoryfilters: CategoryFilterInput) { categoryList(filters: $categoryfilters) { id name }`;
    } else {
      query += `) {`;
    }

    query += ` products (sort: $productSort filter: $productfilters currentPage: $currentPage pageSize: $productPageSize specialCoupon: "${specialCoupon}") {
        total_count
        page_info { page_size }
        items { ...Products }
        aggregations(filter: $aggregationsFilter) { ...AggregationFragment }
      }
    } ${engineOilProductFragment} ${aggregation}`;

    const axiosRes = await axios.get(apiBaseUrl(), {
      paramsSerializer: { indexes: null },
      params: {
        query,
        operationName: "categoryList",
        variables: JSON.stringify(variables),
      },
    });

    return axiosRes.data;
  } catch (error) {
    logError({
      error,
      message: "getEngineOilProducts",
      info: {
        skuList,
        vFilter,
        catIdArr,
        sortOrder,
        queryParams,
        currentPage,
        specialCoupon,
        productPageSize,
      },
    });
    return { data: [], errors: error };
  }
};

export const fetchProductFitment = async (sku) => {
  try {
    const axiosRes = await axios.get(apiBaseUrl(), {
      paramsSerializer: { indexes: null },
      params: {
        query: `query { productFitmentInfo (sku:"${sku}") { label value } }`,
      },
    });
    return axiosRes.data;
  } catch (error) {
    logError({ error, info: { sku }, message: "fetchProductFitment" });
  }
  return [];
};

export const getOptionIdByLabel = async (label) => {
  try {
    // Ensures proper serialization
    const axiosRes = await axios.get(apiBaseUrl(), {
      paramsSerializer: { indexes: null },
      params: {
        query: `query GetOptionIdByLabel {
          getOptionIdByLabel(attributeCode: "quality", optionLabel: "${label}") { option_id }
        }`,
      },
    });
    return axiosRes.data;
  } catch (error) {
    logError({ error, info: { label }, message: "getOptionIdByLabel" });
    return { data: {}, errors: error };
  }
};

export const getFilterableAttributes = async () => {
  try {
    const axiosRes = await axios.get(apiBaseUrl(), {
      params: {
        query: `query { getFilterableAttributes { code label filterable_type } }`,
      },
    });
    return axiosRes.data;
  } catch (error) {
    logError({ error, message: "getFilterableAttributes" });
  }
  return [];
};


export const getProductListwithSku = async (
  sku,
  specialCoupon,
  page = 1,
  pageSize = 8,
) => {
  try {
    let pFilters = { price: { from: 0.01 }, sku: { in: sku } };
    const itemSchema = getProductListFragment();
    let varPayload = {
      currentPage: page,
      productfilters: pFilters,
      productPageSize: pageSize,
    };
    const query = `
      query ProductFilter(
        $currentPage: Int,
        $productPageSize: Int,
        $productfilters: ProductAttributeFilterInput,
      ) {
        products(  
          filter: $productfilters,
          currentPage: $currentPage,
          pageSize: $productPageSize,
          specialCoupon: "${specialCoupon || ""}"
        ) {
          total_count
          items { ...Products }
        
        }
      }
      ${itemSchema}
    `;

    const axiosRes = await axios.get(apiBaseUrl(), {
      paramsSerializer: { indexes: null },
      params: {
        query,
        operationName: "categoryList",
        variables: JSON.stringify(varPayload),
      },
    });
    return axiosRes.data;
  } catch (error) {
    return { data: [], errors: error };
  }
};
